import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LineSearchDropdown } from "@bloomberg/media-ui-dropdown";
import type { LineSearchDropdownProps } from "@bloomberg/media-ui-dropdown/types";
import { debounce } from "@foundation/util";
import { Plus, X } from "@bloomberg/media-ui-icons";
import MediaServicesSearchClient from "@foundation/media-services-search-client";
import { createClasses, TransparentButton } from "@bloomberg/media-ui-core";
import type { WatchlistComparison } from "@bloomberg/octagon-shared";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import { usePageData } from "~context/PageContext";
import { getSessionIdCookie } from "~utils/sessionUtils";
import { SearchQueryResult } from "~types/types";
import {
  useComparisonSettings,
  useCurrentWatchlist,
  usePortfolios,
  WatchlistContext,
} from "~context/WatchlistContext";
import { DEFAULT_COMPARISON_TICKERS } from "~utils/portfolio/getChartColors";

const INPUT_DELAY = 300;
const MAX_COMPARISONS_ALLOWED = 4;
const NO_COMPARISON_TICKER = "--";

type DropdownOptions = LineSearchDropdownProps["options"];

type SearchDropdownProps = {
  readonly id: string;
  readonly disabled: boolean;
  readonly placeholder: string;
  readonly onSelectValue: (optionId: string, value: string) => void;
  readonly fetchSearchResults: (query: string) => Promise<DropdownOptions>;
};

function SearchDropdown(props: SearchDropdownProps) {
  const { id, disabled, placeholder, onSelectValue, fetchSearchResults } =
    props;

  const [suggestions, setSuggestions] = useState<DropdownOptions>([]);

  const clearSuggestions = () => {
    setSuggestions([]);
  };

  const debouncedFetchResults = debounce((searchQuery: string) => {
    if (!searchQuery) {
      clearSuggestions();

      return;
    }

    fetchSearchResults(searchQuery).then((results: DropdownOptions) => {
      if (!results) {
        clearSuggestions();

        return;
      }

      setSuggestions(results);
    });
  }, INPUT_DELAY);

  const onTextChange = (value: string) => {
    debouncedFetchResults(value);
  };

  const handleChange = useCallback(
    (
      option: DropdownOptions[0] | null,
      downshiftHandle: { setState: (value: unknown) => void }
    ) => {
      if (!option?.value) return;

      const { value, optionId } = option;

      onSelectValue(optionId, value);

      const state = {
        inputValue: "",
        selectedItem: { subText: "", value: "", optionId: "" },
      };

      // Workaround to clear text box value after selecting
      downshiftHandle.setState(state);
    },
    [onSelectValue]
  );

  const cx = createClasses([
    styles.searchDropdownContainer,
    {
      [styles.disabled]: disabled,
    },
  ]);

  return (
    <div className={cx}>
      <LineSearchDropdown
        className={styles.searchDropdown}
        onTextChange={onTextChange}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange={handleChange}
        id={id}
        inputProps={{ autocomplete: "off" }}
        variant="default"
        dropdownVariant="markets"
        showDropdownPlusBtn
        disabled={disabled}
        placeholder={placeholder}
        noOptionsText="No Results"
        options={suggestions}
      />
      <Plus className={styles.plusIcon} />
    </div>
  );
}

function normalizeComparison(
  comparisons: WatchlistComparison[]
): WatchlistComparison[] {
  return (
    comparisons?.filter((comparison) => {
      // Ignore no comparison
      if (comparison.comparisonTicker === NO_COMPARISON_TICKER) {
        return false;
      }

      // Ignore empty watchlist (would need to check holdings but we don't have them here)
      return true;
    }) || []
  );
}

export default function ComparisonSettings() {
  const store = React.useContext(WatchlistContext);
  const comparisonSettings = useComparisonSettings();
  const currentWatchlist = useCurrentWatchlist();
  const portfolios = usePortfolios();

  const [disabled, setDisabled] = useState(false);
  const [ignoredWatchlistValues, setIgnoredWatchlistValues] = useState(
    new Set<string>()
  );
  const [ignoredTickerValues, setIgnoredTickerValues] = useState(
    new Set<string>()
  );

  const comparisons = useMemo(
    () => comparisonSettings?.comparisons ?? [],
    [comparisonSettings]
  );

  // Fetch comparison settings on mount and when watchlist changes
  useEffect(() => {
    if (currentWatchlist?.id && store) {
      const { fetchComparisonSettings } = store.getState();

      void fetchComparisonSettings();
    }
  }, [currentWatchlist?.id, store]);

  // Update ignored values when comparisons change
  useEffect(() => {
    const ignoredWatchlistIds = new Set<string>();
    const ignoredTickers = new Set<string>();

    // Add current watchlist id
    if (currentWatchlist?.id) {
      ignoredWatchlistIds.add(currentWatchlist.id.toString());
    }

    // Add previously selected comparison values
    comparisons?.forEach((item) => {
      if (item.comparisonWatchlistId) {
        ignoredWatchlistIds.add(item.comparisonWatchlistId.toString());
      } else if (item.comparisonTicker) {
        ignoredTickers.add(item.comparisonTicker);
      }
    });

    setIgnoredWatchlistValues(ignoredWatchlistIds);
    setIgnoredTickerValues(ignoredTickers);

    // Maximum 4 comparisons are allowed
    setDisabled(
      normalizeComparison(comparisons)?.length >= MAX_COMPARISONS_ALLOWED
    );
  }, [comparisons, currentWatchlist?.id]);

  // Watchlist search
  const userWatchlistOptions = useMemo(() => {
    return portfolios.map((portfolio) => ({
      value: portfolio.id.toString(),
      subText: portfolio.name,
    }));
  }, [portfolios]);

  const fetchWatchlistSearchResults = useCallback(
    (searchString: string): Promise<DropdownOptions> => {
      if (!userWatchlistOptions?.length) {
        return Promise.resolve([]);
      }

      return Promise.resolve(
        userWatchlistOptions
          .filter((item) => {
            // Ignore values that we don't want to show in results
            if (ignoredWatchlistValues.has(item.value)) {
              return false;
            }

            return (
              item.subText.toLowerCase().includes(searchString.toLowerCase()) ||
              item.value.toLowerCase().includes(searchString.toLowerCase())
            );
          })
          .map((item) => ({
            value: item.subText,
            subText: item.subText,
            optionId: item.value,
          }))
      );
    },
    [ignoredWatchlistValues, userWatchlistOptions]
  );

  // Symbol search
  const { countryCode, searchClientURL } = usePageData();
  const searchClient = useMemo(
    () =>
      new MediaServicesSearchClient({
        baseUrl: searchClientURL,
      }),
    [searchClientURL]
  );

  const fetchSymbolSearchResults = useCallback(
    async (searchQuery: string) => {
      const { results: nextSuggestions }: SearchQueryResult =
        await searchClient.lookup({
          query: searchQuery,
          includeBlocklisted: false,
          source: getSessionIdCookie(),
          size: 100,
          country: countryCode,
          types: ["Company", "Index", "Fund", "Currency"],
          boostParents: true,
        });

      if (!nextSuggestions) {
        return [];
      }

      return nextSuggestions
        .filter((item) => {
          // Ignore tickers that we don't want to show in results
          return !ignoredTickerValues.has(item.resource_id);
        })
        .map(({ name, resource_id: resourceId }) => ({
          value: resourceId,
          subText: name,
          optionId: resourceId,
        }));
    },
    [ignoredTickerValues, searchClient, countryCode]
  );

  const handleCompareSelection = useCallback(
    (
      type: "ticker" | "watchlist",
      compareValue: string,
      compareName?: string
    ) => {
      if (!store) return;

      trackClickEvent(
        "watchlistComparisonAdd",
        `add_${type}_comparison`,
        "click",
        "watchlist"
      );

      // Create new comparison
      // New comparison (backend will assign ID and populate comparisonWatchlist)
      const newComparison: WatchlistComparison = {
        compareId: -1,
        watchlistId: currentWatchlist?.id || 0,
        ...(type === "ticker"
          ? { comparisonTicker: compareValue }
          : {
              comparisonWatchlistId: parseInt(compareValue, 10),
            }),
      };

      // Add to existing comparisons
      const updatedComparisons = [...comparisons, newComparison];

      // Update via action
      const { updateComparisonSettings } = store.getState();

      void updateComparisonSettings(updatedComparisons);
    },
    [comparisons, currentWatchlist?.id, store]
  );

  const handleRemoveComparison = useCallback(
    (compareId: number) => {
      if (!store) return;

      trackClickEvent(
        "watchlistComparisonRemove",
        "remove_comparison",
        "click",
        "watchlist"
      );

      // Remove from comparisons
      const updatedComparisons = comparisons.filter(
        (c) => c.compareId !== compareId
      );

      // Update via action
      const { updateComparisonSettings } = store.getState();

      void updateComparisonSettings(updatedComparisons);
    },
    [comparisons, store]
  );

  const handleWatchlistSelection = useCallback(
    (optionId: string, value: string) => {
      handleCompareSelection("watchlist", optionId, value);
    },
    [handleCompareSelection]
  );

  const handleTickerSelection = useCallback(
    (optionId: string) => {
      handleCompareSelection("ticker", optionId);
    },
    [handleCompareSelection]
  );

  const comparisonsRemaining = 4 - normalizeComparison(comparisons).length;
  let comparisonRemainingMessage = "You've reached comparison limit";

  if (comparisonsRemaining === 1) {
    comparisonRemainingMessage = "You can add 1 more comparison";
  } else if (comparisonsRemaining > 1) {
    comparisonRemainingMessage = `You can add ${comparisonsRemaining} more comparisons`;
  }

  return (
    <div className={styles.comparisonSettings}>
      <div className={styles.searchInputs}>
        <SearchDropdown
          id="ticker-search"
          disabled={disabled}
          placeholder="Add Symbol"
          onSelectValue={handleTickerSelection}
          fetchSearchResults={fetchSymbolSearchResults}
        />
        <SearchDropdown
          id="watchlist-search"
          disabled={disabled}
          placeholder="Add Watchlist"
          onSelectValue={handleWatchlistSelection}
          fetchSearchResults={fetchWatchlistSearchResults}
        />
      </div>

      <div className={styles.actionContainer}>
        <span className={styles.actionMessage}>
          {comparisonRemainingMessage}
        </span>
      </div>
    </div>
  );
}
